import React, { useState, useRef, useEffect } from 'react';
import useLanguage from '../../i18n/useLanguage';

// Crisp, lightweight SVG flags for universal cross-platform support
const FLAGS = {
  en: () => (
    <svg width="18" height="13" viewBox="0 0 640 480" style={{ borderRadius: '2px', display: 'inline-block', verticalAlign: 'middle', boxShadow: '0 0 1px rgba(0,0,0,0.3)', flexShrink: 0 }}>
      <path fill="#bd3d44" d="M0 0h640v480H0z"/>
      <path stroke="#fff" strokeWidth="37" d="M0 55.5h640M0 129.5h640M0 203.5h640M0 277.5h640M0 351.5h640M0 425.5h640"/>
      <path fill="#192f5d" d="M0 0h296v259H0z"/>
      <g fill="#fff">
        <circle cx="37" cy="26" r="8"/><circle cx="111" cy="26" r="8"/><circle cx="185" cy="26" r="8"/><circle cx="259" cy="26" r="8"/>
        <circle cx="74" cy="52" r="8"/><circle cx="148" cy="52" r="8"/><circle cx="222" cy="52" r="8"/>
        <circle cx="37" cy="78" r="8"/><circle cx="111" cy="78" r="8"/><circle cx="185" cy="78" r="8"/><circle cx="259" cy="78" r="8"/>
        <circle cx="74" cy="104" r="8"/><circle cx="148" cy="104" r="8"/><circle cx="222" cy="104" r="8"/>
        <circle cx="37" cy="130" r="8"/><circle cx="111" cy="130" r="8"/><circle cx="185" cy="130" r="8"/><circle cx="259" cy="130" r="8"/>
        <circle cx="74" cy="156" r="8"/><circle cx="148" cy="156" r="8"/><circle cx="222" cy="156" r="8"/>
        <circle cx="37" cy="182" r="8"/><circle cx="111" cy="182" r="8"/><circle cx="185" cy="182" r="8"/><circle cx="259" cy="182" r="8"/>
        <circle cx="74" cy="208" r="8"/><circle cx="148" cy="208" r="8"/><circle cx="222" cy="208" r="8"/>
        <circle cx="37" cy="234" r="8"/><circle cx="111" cy="234" r="8"/><circle cx="185" cy="234" r="8"/><circle cx="259" cy="234" r="8"/>
      </g>
    </svg>
  ),
  es: () => (
    <svg width="18" height="13" viewBox="0 0 640 480" style={{ borderRadius: '2px', display: 'inline-block', verticalAlign: 'middle', boxShadow: '0 0 1px rgba(0,0,0,0.3)', flexShrink: 0 }}>
      <path fill="#c60b1e" d="M0 0h640v480H0z"/>
      <path fill="#ffc400" d="M0 120h640v240H0z"/>
      <g transform="translate(140, 240) scale(0.6)">
        <rect x="-40" y="-70" width="80" height="110" rx="20" fill="#c60b1e" stroke="#fff" strokeWidth="4"/>
        <rect x="-30" y="-60" width="60" height="90" rx="10" fill="#ffc400"/>
        <circle cx="0" cy="-15" r="16" fill="#155a94"/>
        <path d="M-55,-90 L55,-90 L40,-50 L-40,-50 Z" fill="#ffc400" stroke="#c60b1e" strokeWidth="4"/>
      </g>
    </svg>
  ),
  fr: () => (
    <svg width="18" height="13" viewBox="0 0 640 480" style={{ borderRadius: '2px', display: 'inline-block', verticalAlign: 'middle', boxShadow: '0 0 1px rgba(0,0,0,0.3)', flexShrink: 0 }}>
      <path fill="#002654" d="M0 0h213.3v480H0z"/>
      <path fill="#fff" d="M213.3 0h213.4v480H213.3z"/>
      <path fill="#ce1126" d="M426.7 0H640v480H426.7z"/>
    </svg>
  ),
  de: () => (
    <svg width="18" height="13" viewBox="0 0 640 480" style={{ borderRadius: '2px', display: 'inline-block', verticalAlign: 'middle', boxShadow: '0 0 1px rgba(0,0,0,0.3)', flexShrink: 0 }}>
      <path fill="#000" d="M0 0h640v160H0z"/>
      <path fill="#dd0000" d="M0 160h640v160H0z"/>
      <path fill="#ffce00" d="M0 320h640v160H0z"/>
    </svg>
  ),
  fil: () => (
    <svg width="18" height="13" viewBox="0 0 640 480" style={{ borderRadius: '2px', display: 'inline-block', verticalAlign: 'middle', boxShadow: '0 0 1px rgba(0,0,0,0.3)', flexShrink: 0 }}>
      <path fill="#0038a8" d="M0 0h640v240H0z"/>
      <path fill="#ce1126" d="M0 240h640v240H0z"/>
      <path fill="#fff" d="M0 0l320 240L0 480z"/>
      <circle cx="100" cy="240" r="38" fill="#fcd116"/>
      <path d="M100 185v110M45 240h110M61 201l78 78M61 279l78-78" stroke="#fcd116" strokeWidth="6"/>
      <polygon points="35,60 40,75 55,75 42,85 47,100 35,90 23,100 28,85 15,75 30,75" fill="#fcd116" transform="scale(0.5) translate(20, 40)"/>
      <polygon points="35,60 40,75 55,75 42,85 47,100 35,90 23,100 28,85 15,75 30,75" fill="#fcd116" transform="scale(0.5) translate(20, 800)"/>
      <polygon points="35,60 40,75 55,75 42,85 47,100 35,90 23,100 28,85 15,75 30,75" fill="#fcd116" transform="scale(0.5) translate(500, 420)"/>
    </svg>
  ),
};

const getDisplayCode = (code) => {
  if (!code) return 'EN';
  const upper = code.toUpperCase();
  if (upper === 'FIL') return 'PH';
  return upper;
};

export default function LanguageSwitcher({ compact = false, fullWidth = false, direction = 'down', className = '' }) {
  const { currentLanguage, languages, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code) => {
    changeLanguage(code);
    setIsOpen(false);
  };

  const currentCode = currentLanguage?.code || 'en';
  const CurrentFlagComponent = FLAGS[currentCode] || FLAGS.en;

  return (
    <div
      ref={dropdownRef}
      className={`rs-lang-switcher-wrapper ${className}`}
      style={{
        position: 'relative',
        display: fullWidth ? 'block' : 'inline-block',
        width: fullWidth ? '100%' : 'auto'
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-label="Select Language"
        className="rs-lang-btn"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '6px',
          padding: '6px 10px',
          borderRadius: '8px',
          border: '1px solid rgba(26, 99, 165, 0.22)',
          backgroundColor: '#ffffff',
          color: '#163A5A',
          fontSize: '13px',
          fontWeight: 700,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          outline: 'none',
          boxShadow: '0 1px 2px rgba(22,58,90,0.06)',
          whiteSpace: 'nowrap',
          width: fullWidth ? '100%' : 'auto',
          boxSizing: 'border-box'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#2AB6E8';
          e.currentTarget.style.backgroundColor = '#F5F9FC';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgba(26, 99, 165, 0.22)';
          e.currentTarget.style.backgroundColor = '#ffffff';
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <CurrentFlagComponent />
          <span style={{ letterSpacing: '0.04em' }}>{getDisplayCode(currentCode)}</span>
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            opacity: 0.7,
            marginLeft: '1px',
            flexShrink: 0
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="rs-lang-menu"
          style={{
            position: 'absolute',
            ...(direction === 'up'
              ? { bottom: 'calc(100% + 6px)' }
              : { top: 'calc(100% + 6px)' }),
            left: 0,
            right: 0,
            width: '100%',
            zIndex: 1200,
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            border: '1px solid rgba(26, 99, 165, 0.14)',
            boxShadow: '0 10px 25px -5px rgba(22, 58, 90, 0.14), 0 8px 10px -6px rgba(22, 58, 90, 0.08)',
            padding: '4px',
            boxSizing: 'border-box'
          }}
        >
          {languages.map((lang) => {
            const isSelected = lang.code === currentCode;
            const ItemFlag = FLAGS[lang.code] || FLAGS.en;
            const displayCode = getDisplayCode(lang.code);

            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => handleSelect(lang.code)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '6px',
                  padding: '7px 6px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: isSelected ? 'rgba(42, 182, 232, 0.14)' : 'transparent',
                  color: isSelected ? '#1A63A5' : '#163A5A',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background-color 0.15s ease',
                  whiteSpace: 'nowrap',
                  boxSizing: 'border-box'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = '#F5F9FC';
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <ItemFlag />
                <span style={{ letterSpacing: '0.04em' }}>
                  {displayCode}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
